// Whether the server can be reached: the device's own flag (navigator.onLine) and whether the last request got an
// answer at all (a phone can be "online" on a network that reaches nothing). The app reads mail from the offline
// copy while it cannot reach the server.

import { useEffect, useState } from "preact/hooks";

/** A request that got no answer: no network, or the server could not be reached. */
export class NetworkError extends Error {
  constructor(message = "No connection to the server") {
    super(message);
    this.name = "NetworkError";
  }
}

let answered = true;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((fn) => fn());
}

if (typeof window !== "undefined") {
  addEventListener("online", () => {
    answered = true; // worth trying again
    notify();
  });
  addEventListener("offline", notify);
}

/** Called by every request: true when it got an answer (an error status too), false when it got none. */
export function reportAnswer(ok: boolean): void {
  if (ok === answered) return;
  answered = ok;
  notify();
}

export function isOnline(): boolean {
  return (typeof navigator === "undefined" || navigator.onLine !== false) && answered;
}

/** fetch() rejects with a TypeError when there is no answer; NetworkError is what the API module turns it into. */
export function isNetworkError(e: unknown): boolean {
  return e instanceof NetworkError || e instanceof TypeError;
}

export function onConnectionChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => void listeners.delete(fn);
}

export function useOnline(): boolean {
  const [online, setOnline] = useState(isOnline);
  useEffect(() => onConnectionChange(() => setOnline(isOnline())), []);
  return online;
}
